import React from 'react';
import { reviewContent } from '../data/reviewContent';
import LeadForm from './LeadForm';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAward, FiStar, FiExternalLink, FiCheck, FiX, FiInfo, FiArrowRight } = FiIcons;

const ReviewPage = () => {
  const { hero, reputationSection, quickAnswer, sections, prosCons } = reviewContent;

  return (
    <div className="pt-20 bg-slate-50 min-h-screen">
      {/* Hero */}
      <section className="bg-slate-900 py-20 text-white text-center px-4">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-400/10 text-yellow-400 rounded-full text-sm font-bold mb-6 border border-yellow-400/20">
            <SafeIcon icon={FiAward} />
            <span>Verified 2026 Review</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 font-serif text-white">{hero.headline}</h1>
          <p className="text-xl text-slate-300 mb-10 leading-relaxed">{hero.subheadline}</p>
          <a href="#lead-form" className="inline-flex items-center px-8 py-4 bg-yellow-400 text-slate-900 font-bold rounded-lg hover:bg-yellow-300 transition-all">
            {hero.ctaText} <SafeIcon icon={FiArrowRight} className="ml-2" />
          </a>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          <div className="lg:col-span-8">
            {/* Quick Answer */}
            <div className="bg-white border-2 border-yellow-400 p-8 rounded-2xl mb-12 shadow-lg">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <SafeIcon icon={FiInfo} className="text-yellow-500" />
                Quick Summary
              </h3>
              <p className="text-slate-700 italic text-lg leading-relaxed">{quickAnswer.text}</p>
            </div>

            {/* Reputation Section */}
            <div id="reputation" className="mb-16">
              <h2 className="text-3xl font-bold font-serif mb-4">{reputationSection.title}</h2>
              <p className="text-slate-600 text-lg mb-8 leading-relaxed">{reputationSection.description}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {reputationSection.stats.map((stat, i) => (
                  <a key={i} href={stat.link} target="_blank" rel="noopener noreferrer" className="group bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:border-yellow-400 hover:shadow-md transition-all flex flex-col justify-between">
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">{stat.platform}</span>
                      <SafeIcon icon={FiExternalLink} className="text-slate-300 group-hover:text-yellow-500" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-slate-900 mb-1 flex items-center gap-2">
                        {stat.rating}
                        <div className="flex text-yellow-400 text-xs">
                          {[...Array(5)].map((_, i) => <SafeIcon key={i} icon={FiStar} />)}
                        </div>
                      </div>
                      <div className="text-slate-500 text-sm">{stat.count}</div>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Article Content */}
            <div className="prose-financial">
              {sections.map(s => (
                <div key={s.id} className="mb-12">
                  <h3 className="text-2xl font-bold mb-4">{s.title}</h3>
                  <p className="text-slate-700 leading-relaxed text-lg">{s.content}</p>
                </div>
              ))}
            </div>

            {/* Pros & Cons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
              <div className="bg-green-50 p-8 rounded-2xl border border-green-100">
                <h4 className="font-bold text-green-900 mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiCheck} className="text-green-600" /> Pros
                </h4>
                <ul className="space-y-2 text-sm text-green-800">
                  {prosCons.pros.map((p, i) => <li key={i}>• {p}</li>)}
                </ul>
              </div>
              <div className="bg-red-50 p-8 rounded-2xl border border-red-100">
                <h4 className="font-bold text-red-900 mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiX} className="text-red-600" /> Cons
                </h4>
                <ul className="space-y-2 text-sm text-red-800">
                  {prosCons.cons.map((c, i) => <li key={i}>• {c}</li>)}
                </ul>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4">
            <div className="sticky top-28 space-y-6">
              <LeadForm 
                title="Get the Free Guide"
                description="Join over 100,000 investors who have requested the Augusta Gold IRA kit."
                ctaText="Send My Guide Now"
              />

              <div className="bg-slate-900 text-white p-8 rounded-2xl shadow-xl">
                <h4 className="font-bold mb-4 text-yellow-400 font-serif">Verified Ratings</h4>
                <div className="space-y-4">
                  <div>
                    <div className="text-2xl font-bold">A+</div>
                    <div className="text-slate-400 text-[10px] uppercase tracking-wider">BBB Rating</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">AAA</div>
                    <div className="text-slate-400 text-[10px] uppercase tracking-wider">BCA Rating</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-yellow-400 flex items-center gap-1">
                      <SafeIcon icon={FiStar} /> 4.5/5
                    </div>
                    <div className="text-slate-400 text-[10px] uppercase tracking-wider">Trustpilot Rating</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ReviewPage;